import { registerIndicator, type IndicatorDrawParams, type KLineData } from 'klinecharts'
import { registerOnce } from './registrationUtils'

/**
 * Shade an arbitrary [fromTs, toTs] timestamp span behind the bars — e.g. the open->checkpoint
 * window the Pattern Explorer matches on. Sibling to sessionHighlight — both are seeded as the
 * backmost background layer (see BACKGROUND_SHADING_Z_LEVEL in ChartManager), but this band is a
 * fixed timestamp range from `extendData`, not a session classification.
 */
export interface RangeHighlightConfig {
  fromTs: number
  toTs: number
  /** Translucent fill for the band. */
  color: string
  /** Opaque accent for the edges, dashed midline, and label (optional; falls back to color). */
  lineColor?: string
  /** Label drawn on the midline, e.g. "open → 11am" (optional). */
  label?: string
}

const INDICATOR_NAME = 'RANGE_HIGHLIGHT'

/** Narrow canvas surface for fake-ctx testing. Decoration methods are optional so a minimal
 *  fake (fill only) still exercises the fill path; the real CanvasRenderingContext2D has them. */
export type RangeHighlightCtx = {
  fillStyle: string | CanvasGradient | CanvasPattern
  strokeStyle?: string | CanvasGradient | CanvasPattern
  lineWidth?: number
  font?: string
  textAlign?: CanvasTextAlign
  textBaseline?: CanvasTextBaseline
  save: () => void
  restore: () => void
  fillRect: (x: number, y: number, w: number, h: number) => void
  setLineDash?: (segments: number[]) => void
  beginPath?: () => void
  moveTo?: (x: number, y: number) => void
  lineTo?: (x: number, y: number) => void
  stroke?: () => void
  fillText?: (text: string, x: number, y: number) => void
  measureText?: (text: string) => { width: number }
}

export interface RangeHighlightDrawParams {
  ctx: RangeHighlightCtx
  kLineDataList: Array<{ timestamp: number } | undefined>
  visibleRange: { from: number; to: number }
  bounding: { height: number }
  barSpace: { halfBar: number }
  xAxis: { convertToPixel: (dataIndex: number) => number }
  indicator: { extendData?: unknown }
}

/** Shade the visible bars in [fromTs, toTs] as a SELECTED region: translucent fill + bounding
 *  edges + a dashed midline carrying the timeframe label. Draws one span across the min..max
 *  in-range index (our use is one contiguous open->checkpoint window). */
export function drawRangeHighlight(params: RangeHighlightDrawParams): boolean {
  const { ctx, kLineDataList, visibleRange, bounding, barSpace, xAxis, indicator } = params
  const config = indicator.extendData as RangeHighlightConfig | undefined
  if (!config || config.toTs <= config.fromTs) return false

  const { from, to } = visibleRange
  let firstIdx = -1
  let lastIdx = -1
  for (let i = from; i < to; i++) {
    const data = kLineDataList[i]
    if (data && data.timestamp >= config.fromTs && data.timestamp <= config.toTs) {
      if (firstIdx < 0) firstIdx = i
      lastIdx = i
    }
  }
  if (firstIdx < 0) return false

  ctx.save()
  const x1 = xAxis.convertToPixel(firstIdx) - barSpace.halfBar
  const x2 = xAxis.convertToPixel(lastIdx) + barSpace.halfBar
  const h = bounding.height
  const line = config.lineColor ?? config.color

  // Translucent fill
  ctx.fillStyle = config.color
  ctx.fillRect(x1, 0, x2 - x1, h)

  // Decorations — only if the ctx supports stroking/text (real canvas; skipped by the test fake).
  if (typeof ctx.beginPath === 'function' && typeof ctx.stroke === 'function') {
    const mid = h / 2
    // Bounding edges (solid) so the region reads as selected, not just tinted.
    ctx.strokeStyle = line
    ctx.lineWidth = 1
    ctx.setLineDash?.([])
    ctx.beginPath()
    ctx.moveTo!(x1, 0)
    ctx.lineTo!(x1, h)
    ctx.moveTo!(x2, 0)
    ctx.lineTo!(x2, h)
    ctx.stroke()
    // Dashed midline across the region.
    ctx.setLineDash?.([5, 4])
    ctx.beginPath()
    ctx.moveTo!(x1, mid)
    ctx.lineTo!(x2, mid)
    ctx.stroke()
    ctx.setLineDash?.([])

    // Timeframe label centered on the midline.
    if (config.label && typeof ctx.fillText === 'function') {
      const cx = (x1 + x2) / 2
      ctx.font = '11px sans-serif'
      ctx.textAlign = 'center'
      ctx.textBaseline = 'bottom'
      ctx.fillStyle = line
      ctx.fillText(config.label, cx, mid - 3)
    }
  }

  ctx.restore()
  // false: background only — must not suppress the bars.
  return false
}

export const registerRangeHighlightIndicator = registerOnce(() => {
  registerIndicator({
    name: INDICATOR_NAME,
    shortName: '',
    figures: [],
    calc: (dataList: KLineData[]) => dataList.map(() => ({})),
    draw: (params: IndicatorDrawParams) =>
      drawRangeHighlight(params as unknown as RangeHighlightDrawParams),
  })
})

export const RANGE_HIGHLIGHT_NAME = INDICATOR_NAME
